import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GuideTask,
  QuickStartService,
} from '../../core/services/quick-start.service';

type TaskFilter = 'all' | 'pending' | 'completed';

/**
 * Quick Start Guide side panel.
 *
 * Renders exactly what `GET /api/v1/tenant/guide` reports. The panel computes no
 * completion of its own: the server is the only place that knows whether a task has
 * already been stamped, and a client-side rule would let the panel show a task as
 * open after the platform had closed it for good.
 *
 * Rendered as an inline layout panel, not an overlay. The parent layout keeps it
 * mounted and animates its width, so `open` is watched only to decide when to
 * refetch — a fresh load on every open is what makes a finished wizard show up
 * without a page reload.
 */
@Component({
  selector: 'app-quick-start-guide',
  standalone: true,
  imports: [RouterLink],
  template: `
    <div class="panel-root">
      <!-- Header -->
      <div class="panel-header">
        <h2 class="panel-title">Quick Start Guide</h2>
        <div class="panel-header-actions">
          <button
            type="button"
            class="panel-icon-btn"
            (click)="load()"
            [disabled]="loading()"
            aria-label="Refresh quick start tasks"
          >
            <i
              class="bi"
              [class.bi-arrow-clockwise]="!loading()"
              [class.bi-arrow-repeat]="loading()"
              aria-hidden="true"
            ></i>
          </button>
          <button
            type="button"
            class="panel-icon-btn"
            (click)="close()"
            aria-label="Close quick start guide"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <!-- Loading state -->
      @if (loading() && tasks().length === 0) {
        <div class="panel-loading" role="status">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading quick start tasks...</span>
          </div>
        </div>
      }

      <!-- Error state -->
      @if (!loading() && error(); as err) {
        <div class="panel-error" role="alert">
          <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
          <p>{{ err }}</p>
          <button type="button" class="btn-retry" (click)="load()">Try Again</button>
        </div>
      }

      <!-- Content -->
      @if (!loading() && !error()) {
        <div class="panel-body">
          <!-- Overall progress across task groups -->
          <div class="guide-progress">
            <span class="guide-progress-count">
              {{ completedTaskCount() }} of {{ totalCount() }}
            </span>
            <div
              class="progress"
              role="progressbar"
              [attr.aria-valuenow]="progressPercent()"
              aria-valuemin="0"
              aria-valuemax="100"
              [attr.aria-label]="completedTaskCount() + ' of ' + totalCount() + ' tasks completed'"
            >
              <div class="progress-bar" [style.width.%]="progressPercent()"></div>
            </div>
          </div>

          <!-- Filter tabs -->
          <div class="filter-tabs" role="tablist" aria-label="Task filters">
            @for (tab of tabs; track tab.value) {
              <button
                type="button"
                class="filter-tab"
                role="tab"
                [attr.aria-selected]="filter() === tab.value"
                [class.active]="filter() === tab.value"
                (click)="setFilter(tab.value)"
              >
                {{ tab.label }}
                <span class="tab-count">{{ counts()[tab.value] }}</span>
              </button>
            }
          </div>

          <!-- Empty state -->
          @if (filteredTasks().length === 0) {
            <div class="empty-state">
              <i class="bi bi-check2-circle" aria-hidden="true"></i>
              <p>{{ emptyMessage() }}</p>
            </div>
          }

          <!-- Task accordion -->
          @if (filteredTasks().length > 0) {
            <div class="task-list" role="list">
              @for (task of filteredTasks(); track task.code) {
                <div class="task-card" role="listitem">
                  <button
                    type="button"
                    class="task-summary"
                    [attr.aria-expanded]="isExpanded(task.code)"
                    [attr.aria-controls]="'task-body-' + task.code"
                    (click)="toggleTask(task.code)"
                  >
                    @if (!task.completed) {
                      <span
                        class="task-status"
                        [attr.aria-label]="
                          task.completedSubtasks + ' of ' + task.totalSubtasks + ' subtasks completed'
                        "
                      >
                        <span class="task-status-text">
                          {{ task.completedSubtasks }}/{{ task.totalSubtasks }}
                        </span>
                      </span>
                    }

                    <span class="task-info">
                      <span class="task-title">{{ task.title }}</span>
                      <span class="task-meta">
                        {{ task.completedSubtasks }} of {{ task.totalSubtasks }} subtasks completed
                      </span>
                      @if (task.targetProfile; as target) {
                        <span class="task-target">
                          Working toward: {{ target.displayName }}
                        </span>
                      }
                    </span>

                    @if (task.completed) {
                      <span class="task-done" aria-label="Task completed">
                        <i class="bi bi-check" aria-hidden="true"></i>
                      </span>
                    }

                    <i
                      class="bi task-chevron"
                      [class.bi-chevron-down]="!isExpanded(task.code)"
                      [class.bi-chevron-up]="isExpanded(task.code)"
                      aria-hidden="true"
                    ></i>
                  </button>

                  @if (isExpanded(task.code)) {
                    <div [id]="'task-body-' + task.code" class="task-body">
                      <ul class="subtask-list" role="list">
                        @for (subtask of task.subtasks; track subtask.code) {
                          <li class="subtask-item" [class.completed]="subtask.completed">
                            <a
                              class="subtask-link"
                              [routerLink]="subtask.path"
                              (click)="onSubtaskNavigate()"
                            >
                              <i
                                class="bi subtask-icon"
                                [class.bi-check-circle-fill]="subtask.completed"
                                [class.bi-circle]="!subtask.completed"
                                aria-hidden="true"
                              ></i>
                              <span class="subtask-label">{{ subtask.label }}</span>
                            </a>
                          </li>
                        }
                      </ul>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      height: 100%;
    }

    .panel-root {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid var(--theme-border-color);
      flex-shrink: 0;
    }

    .panel-title {
      font-size: 1.125rem;
      font-weight: 700;
      color: var(--theme-heading-color);
      margin: 0;
    }

    .panel-header-actions {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
    }

    .panel-icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--theme-muted-color);
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .panel-icon-btn:hover:not(:disabled) {
      background: #f1f5f9;
      color: var(--theme-body-color);
    }

    .panel-icon-btn:disabled {
      cursor: default;
      opacity: 0.6;
    }

    .panel-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      min-height: 200px;
    }

    .panel-error {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      padding: 2rem;
      text-align: center;
      color: var(--theme-danger);
    }

    .panel-error i {
      font-size: 2rem;
    }

    .panel-error p {
      margin: 0;
      color: var(--theme-body-color);
      font-size: 0.875rem;
    }

    .btn-retry {
      padding: 0.5rem 1rem;
      background: var(--theme-primary);
      color: #ffffff;
      border: none;
      border-radius: 0.375rem;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .btn-retry:hover {
      background: var(--theme-primary-hover);
    }

    .panel-body {
      flex: 1;
      overflow-y: auto;
      padding: 1.25rem 1.5rem 1.5rem;
    }

    .guide-progress {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1.25rem;
    }

    .guide-progress-count {
      flex-shrink: 0;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--theme-body-color);
    }

    .guide-progress .progress {
      flex: 1;
      height: 0.5rem;
      background: #e2e8f0;
      border-radius: 50rem;
      overflow: hidden;
      margin: 0;
    }

    .guide-progress .progress-bar {
      height: 100%;
      background: var(--theme-success);
      border-radius: 50rem;
      transition: width 0.3s ease;
    }

    .filter-tabs {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      margin-bottom: 1rem;
      border-bottom: 1px solid var(--theme-border-color);
    }

    .filter-tab {
      position: relative;
      padding: 0.625rem 0.75rem;
      background: none;
      border: none;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--theme-muted-color);
      cursor: pointer;
      transition: color 0.15s ease;
    }

    .filter-tab:hover {
      color: var(--theme-body-color);
    }

    .filter-tab.active {
      color: var(--theme-primary);
    }

    .filter-tab.active::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1px;
      height: 2px;
      background: var(--theme-primary);
      border-radius: 2px 2px 0 0;
    }

    .tab-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1.25rem;
      height: 1.25rem;
      padding: 0 0.375rem;
      margin-left: 0.375rem;
      background: #e2e8f0;
      color: var(--theme-muted-color);
      border-radius: 50rem;
      font-size: 0.6875rem;
      font-weight: 700;
    }

    .filter-tab.active .tab-count {
      background: var(--theme-primary-light);
      color: var(--theme-primary);
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      padding: 2.5rem 1rem;
      text-align: center;
      color: var(--theme-muted-color);
    }

    .empty-state i {
      font-size: 2rem;
    }

    .empty-state p {
      margin: 0;
      font-size: 0.875rem;
    }

    .task-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .task-card {
      background: var(--theme-card-bg);
      border: 1px solid var(--theme-border-color);
      border-radius: 0.75rem;
      overflow: hidden;
    }

    .task-summary {
      display: flex;
      align-items: center;
      gap: 0.875rem;
      width: 100%;
      padding: 1rem 1.25rem;
      background: none;
      border: none;
      text-align: left;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .task-summary:hover {
      background: #f8fafc;
    }

    .task-status {
      flex-shrink: 0;
      width: 2rem;
      height: 2rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: #f1f5f9;
      color: var(--theme-muted-color);
      border: 2px solid #e2e8f0;
      font-size: 0.6875rem;
      font-weight: 700;
    }

    .task-status-text {
      line-height: 1;
    }

    .task-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    .task-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--theme-heading-color);
    }

    .task-meta {
      font-size: 0.8125rem;
      color: var(--theme-muted-color);
    }

    .task-target {
      font-size: 0.8125rem;
      color: var(--theme-muted-color);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .task-done {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 50%;
      background: var(--theme-success);
      color: #ffffff;
      font-size: 0.75rem;
    }

    .task-chevron {
      flex-shrink: 0;
      font-size: 0.875rem;
      color: var(--theme-muted-color);
    }

    .task-body {
      padding: 0 1.25rem 1rem 1.25rem;
      border-top: 1px solid var(--theme-border-color);
    }

    .subtask-list {
      list-style: none;
      margin: 0;
      padding: 0.75rem 0 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .subtask-item {
      font-size: 0.8125rem;
    }

    .subtask-link {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      color: var(--theme-muted-color);
      text-decoration: none;
    }

    .subtask-link:hover .subtask-label {
      color: var(--theme-primary);
      text-decoration: underline;
    }

    .subtask-item.completed .subtask-link {
      color: var(--theme-body-color);
    }

    .subtask-item.completed .subtask-label {
      text-decoration: line-through;
    }

    .subtask-icon {
      font-size: 1rem;
      flex-shrink: 0;
    }

    .subtask-item .bi-check-circle-fill {
      color: var(--theme-success);
    }

    .subtask-item .bi-circle {
      color: #cbd5e1;
    }

    @media (max-width: 576px) {
      .panel-body {
        padding: 1rem;
      }

      .panel-header {
        padding: 1rem;
      }
    }
  `,
})
export class QuickStartGuideDrawerComponent {
  /** Drives refetching only. The parent keeps the panel mounted for the animation. */
  readonly open = input(false);
  readonly closed = output<void>();
  /** Whether any task is still pending — the header's rocket dot reads this. */
  readonly pendingChange = output<boolean>();

  private readonly guide = inject(QuickStartService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly tasks = signal<GuideTask[]>([]);
  readonly filter = signal<TaskFilter>('all');
  readonly expandedIds = signal<Set<string>>(new Set());

  readonly tabs: { label: string; value: TaskFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Completed', value: 'completed' },
  ];

  constructor() {
    // The panel is mounted for the whole app lifetime, so opening it is the refresh
    // signal. Without this a user who finishes the create-profile wizard comes back
    // to the numbers from whenever they first opened it.
    effect(() => {
      if (this.open()) {
        this.load();
      }
    });

    // The header's rocket dot mirrors pending state, and this panel is the only
    // component that talks to the guide API, so it reports upward.
    effect(() => {
      this.pendingChange.emit(this.counts().pending > 0);
    });

    // One eager load so the dot is correct before the first panel open.
    this.load();
  }

  readonly counts = computed(() => {
    const tasks = this.tasks();
    const completed = tasks.filter((t) => t.completed).length;
    return {
      all: tasks.length,
      pending: tasks.length - completed,
      completed,
    };
  });

  readonly completedTaskCount = computed(() => this.counts().completed);
  readonly totalCount = computed(() => this.counts().all);

  readonly progressPercent = computed(() => {
    const total = this.totalCount();
    if (total === 0) return 0;
    return Math.round((this.completedTaskCount() / total) * 100);
  });

  readonly filteredTasks = computed(() => {
    const tasks = this.tasks();
    const f = this.filter();
    if (f === 'all') return tasks;
    return tasks.filter((t) => (f === 'completed' ? t.completed : !t.completed));
  });

  readonly emptyMessage = computed(() => {
    const f = this.filter();
    if (f === 'pending') return 'Nothing left to do. Every task is completed.';
    if (f === 'completed') return 'No completed tasks yet.';
    return 'No quick start tasks are available for this account.';
  });

  load(): void {
    this.loading.set(true);
    this.error.set(null);

    this.guide
      .get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.tasks.set(result.tasks);
          this.expandedIds.set(
            // A pending task is the work, so it is open by default; a completed one
            // is collapsed until the user wants to see what was done.
            new Set(
              result.tasks.filter((t) => !t.completed).map((t) => t.code),
            ),
          );
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.error.set(
            err?.error?.message ?? 'Could not load the quick start guide.',
          );
        },
      });
  }

  /** Navigating into the app means the user went to do the work. */
  onSubtaskNavigate(): void {
    this.closed.emit();
  }

  close(): void {
    this.closed.emit();
  }

  setFilter(filter: TaskFilter): void {
    this.filter.set(filter);
  }

  toggleTask(taskCode: string): void {
    this.expandedIds.update((set) => {
      const next = new Set(set);
      if (next.has(taskCode)) {
        next.delete(taskCode);
      } else {
        next.add(taskCode);
      }
      return next;
    });
  }

  isExpanded(taskCode: string): boolean {
    return this.expandedIds().has(taskCode);
  }
}
